// src/pages/StateAdminReport/AllStateAdminForCommissionTransactions.jsx
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import HandshakeIcon from '@mui/icons-material/Handshake';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';

// Helper to format State Head ID
const formatStateHeadId = (id, stateHeadIdField) => {
  if (stateHeadIdField && stateHeadIdField.trim() !== '') return stateHeadIdField;
  if (!id) return 'N/A';
  const str = String(id).trim();
  return str.slice(-6).toUpperCase();
};

const AllStateAdminForCommissionTransactions = () => {
  const { can } = usePermissions();
  const [userLevel, setUserLevel] = useState(null);
  const [userId, setUserId] = useState('');
  const [stateHeads, setStateHeads] = useState([]);
  const [balances, setBalances] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Settlement Dialog State
  const [settlementModalOpen, setSettlementModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedStateHead, setSelectedStateHead] = useState(null);
  const [settlementDate, setSettlementDate] = useState(() => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }));
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [transactionIdInput, setTransactionIdInput] = useState('');
  const [remarksInput, setRemarksInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccessMsg, setSubmitSuccessMsg] = useState('');

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserLevel(decoded.level);
        setUserId(decoded.userId);
      } catch (err) {
        console.error('Invalid token', err);
      }
    }
  }, []);


  const fetchStateHeadsAndBalances = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = sessionStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/users/by-level`, {
        params: { level: 2, requesterLevel: userLevel || 1, userId },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.status === 200 && Array.isArray(res.data)) {
        setStateHeads(res.data);
        
        // Fetch commission balance for each State Head
        const balMap = {};
        await Promise.all(
          res.data.map(async (sh) => {
            const shKey = String(sh._id || sh.id || '');
            try {
              const bRes = await axios.get(`${API_BASE_URL}/stateAdminCommission/balance/${shKey}`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              if (bRes.status === 200 && bRes.data?.data) {
                balMap[shKey] = bRes.data.data.totalBalance || 0;
              } else {
                balMap[shKey] = 0;
              }
            } catch (bErr) {
              balMap[shKey] = 0;
            }
          })
        );
        setBalances(balMap);
      } else {
        setStateHeads([]);
      }
    } catch (err) {
      console.error('Error fetching state heads:', err);
      setError('Failed to fetch State Head records.');
    } finally {
      setLoading(false);
    }
  }, [userLevel, userId]);

  useEffect(() => {
    if (userLevel !== null) {
      fetchStateHeadsAndBalances();
    }
  }, [userLevel, userId, fetchStateHeadsAndBalances]);

  // Search Filter by State Head ID / Name / Mobile / Email
  const filteredStateHeads = useMemo(() => {
    if (!searchTerm.trim()) return stateHeads;
    const term = searchTerm.toLowerCase().trim();
    return stateHeads.filter((sh) => {
      const shId = (sh.stateHeadId || sh.displayId || formatStateHeadId(sh._id || sh.id, sh.stateHeadId)).toLowerCase();
      const fName = (sh.firstName || '').toLowerCase();
      const lName = (sh.lastName || '').toLowerCase();
      const fullName = `${fName} ${lName}`;
      const mobile = String(sh.phoneNumber || sh.mobile || '').toLowerCase();
      const email = (sh.email || '').toLowerCase();

      return (
        shId.includes(term) ||
        fName.includes(term) ||
        lName.includes(term) ||
        fullName.includes(term) ||
        mobile.includes(term) ||
        email.includes(term)
      );
    });
  }, [stateHeads, searchTerm]);

  // CSV Export (permission gated)
  const handleExportCSV = () => {
    if (!can('stateHead.settlementsHandle.export')) return;
    if (filteredStateHeads.length === 0) return;

    const headers = [
      'SL No',
      'State Head ID',
      'First Name',
      'Last Name',
      'Mobile Number',
      'Email',
      'Available Commission Balance (₹)'
    ];

    const csvRows = [headers.join(',')];

    filteredStateHeads.forEach((sh, idx) => {
      const displayShId = sh.stateHeadId || sh.displayId || formatStateHeadId(sh._id || sh.id, sh.stateHeadId);
      const availBal = balances[sh._id || sh.id] || 0;

      const row = [
        idx + 1,
        `"${displayShId}"`,
        `"${sh.firstName || ''}"`,
        `"${sh.lastName || ''}"`,
        `"${sh.phoneNumber || sh.mobile || ''}"`,
        `"${sh.email || ''}"`,
        availBal
      ];

      csvRows.push(row.join(','));
    });

    const csvContent = 'data:text/csv;charset=utf-8,' + csvRows.join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `STATE_HEAD_Settlements_Handle_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Open Settlement Modal Dialog
  const handleOpenSettlementModal = (sh) => {
    if (!can('stateHead.settlementsHandle.withdraw')) return;
    setSelectedStateHead(sh);
    setWithdrawalAmount('');
    setTransactionIdInput('');
    setRemarksInput('');
    setSettlementDate(new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }));
    setSubmitSuccessMsg('');
    setSettlementModalOpen(true);
  };

  // Helper to resolve available balance for State Head
  const getAvailableBalance = (sh) => {
    if (!sh) return 0;
    if (typeof sh.totalBalance !== 'undefined' && sh.totalBalance !== null) return Number(sh.totalBalance);
    if (typeof sh.availableBalance !== 'undefined' && sh.availableBalance !== null) return Number(sh.availableBalance);
    const key = String(sh._id || sh.id || '');
    return Number(balances[key] ?? balances[sh._id] ?? 0);
  };

  // Open Confirmation Modal
  const handleOpenConfirm = () => {
    if (!selectedStateHead) return;
    const amountNum = parseFloat(withdrawalAmount);
    const currentBal = getAvailableBalance(selectedStateHead);

    if (!amountNum || amountNum <= 0) {
      alert('Please enter a valid withdrawal amount.');
      return;
    }

    if (amountNum > currentBal) {
      alert(`Withdrawal amount (₹${amountNum}) cannot exceed available balance (₹${currentBal}).`);
      return;
    }

    setConfirmModalOpen(true);
  };

  // Execute Settlement after confirmation
  const handleConfirmAndSubmit = async () => {
    setConfirmModalOpen(false);
    await handleSubmitSettlement();
  };

  // Submit Settlement Form
  const handleSubmitSettlement = async () => {
    if (!can('stateHead.settlementsHandle.withdraw')) return;
    if (!selectedStateHead) return;
    const amountNum = parseFloat(withdrawalAmount);

    try {
      setSubmitting(true);
      const token = sessionStorage.getItem('token');
      const res = await axios.post(
        `${API_BASE_URL}/stateAdminCommission/withdraw/${selectedStateHead._id || selectedStateHead.id}`,
        {
          amount: amountNum,
          transactionDate: settlementDate,
          transactionId: transactionIdInput.trim(),
          reason: remarksInput.trim() || 'Settlement',
          adminNotes: remarksInput.trim() || 'Commission Settlement'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (res.status === 200 && res.data?.status === 'SUCCESS') {
        setSubmitSuccessMsg('Commission settlement completed successfully!');
        setTimeout(() => {
          setSettlementModalOpen(false);
          fetchStateHeadsAndBalances();
        }, 1500);
      } else {
        alert(res.data?.message || 'Failed to submit settlement.');
      }
    } catch (err) {
      console.error('Error processing settlement:', err);
      alert(err.response?.data?.message || err.message || 'Error processing settlement.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 3, position: 'relative' }}>
      <BackButton />
      <br /><br />

      {/* Header & Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          STATE HEAD Settlements Handle
        </Typography>

        {/* CSV Export (permission gated) */}
        {can('stateHead.settlementsHandle.export') && (
          <Button
            variant="contained"
            color="success"
            startIcon={<DownloadIcon />}
            onClick={handleExportCSV}
            disabled={filteredStateHeads.length === 0}
          >
            Export CSV
          </Button>
        )}
      </Box>

      {/* Search Bar */}
      <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SearchIcon color="action" />
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Search by State Head ID, Name, Mobile, or Email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2, maxHeight: 'calc(100vh - 260px)', minHeight: 450, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead sx={{ backgroundColor: 'primary.main' }}>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>SL No</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>State Head ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>First Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Last Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Mobile Number</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Email</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Available Balance (₹)</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredStateHeads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    No State Head records found.
                  </TableCell>
                </TableRow>
              ) : (
                filteredStateHeads.map((sh, idx) => {
                  const displayShId = sh.stateHeadId || sh.displayId || formatStateHeadId(sh._id || sh.id, sh.stateHeadId);
                  const availBal = getAvailableBalance(sh);

                  return (
                    <TableRow key={sh._id || sh.id || idx} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        {displayShId}
                      </TableCell>
                      <TableCell>{sh.firstName || 'N/A'}</TableCell>
                      <TableCell>{sh.lastName || 'N/A'}</TableCell>
                      <TableCell>{sh.phoneNumber || sh.mobile || 'N/A'}</TableCell>
                      <TableCell>{sh.email || 'N/A'}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'success.main' }}>
                        ₹{availBal.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {can('stateHead.settlementsHandle.withdraw') ? (
                          <Button
                            variant="contained"
                            color="warning"
                            size="small"
                            startIcon={<HandshakeIcon />}
                            onClick={() => handleOpenSettlementModal(sh)}
                          >
                            Settle Commission
                          </Button>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Commission Settlement Modal Dialog */}
      <Dialog open={settlementModalOpen} onClose={() => setSettlementModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
          Settle Commission - {selectedStateHead ? (selectedStateHead.stateHeadId || formatStateHeadId(selectedStateHead._id || selectedStateHead.id, selectedStateHead.stateHeadId)) : ''}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {submitSuccessMsg ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              {submitSuccessMsg}
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
              {/* Field 1: Show State Head ID */}
              <TextField
                label="Show State Head ID"
                value={selectedStateHead ? (selectedStateHead.stateHeadId || formatStateHeadId(selectedStateHead._id || selectedStateHead.id, selectedStateHead.stateHeadId)) : 'N/A'}
                InputProps={{ readOnly: true }}
                fullWidth
                size="small"
                variant="outlined"
              />

              {/* Field 2: Show Available balance */}
              <TextField
                label="Show Available Balance"
                value={`₹${getAvailableBalance(selectedStateHead).toLocaleString()}`}
                InputProps={{ readOnly: true }}
                fullWidth
                size="small"
                variant="outlined"
                sx={{ '& input': { color: 'success.main', fontWeight: 'bold' } }}
              />

              {/* Field 3: Select Transaction Date */}
              <TextField
                label="Select Transaction Date"
                type="date"
                value={settlementDate}
                onChange={(e) => setSettlementDate(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />

              {/* Field 4: Enter Withdrawal amount */}
              <TextField
                label="Enter Withdrawal Amount (₹)"
                type="number"
                value={withdrawalAmount}
                onChange={(e) => setWithdrawalAmount(e.target.value)}
                placeholder="e.g. 5000"
                fullWidth
                size="small"
                required
              />

              {/* Field 5: Enter Transaction ID */}
              <TextField
                label="Enter Transaction ID"
                value={transactionIdInput}
                onChange={(e) => setTransactionIdInput(e.target.value)}
                placeholder="e.g. UTR123456789 or Ref No"
                fullWidth
                size="small"
              />

              {/* Field 6: Enter Remarks */}
              <TextField
                label="Enter Remarks"
                value={remarksInput}
                onChange={(e) => setRemarksInput(e.target.value)}
                placeholder="Enter settlement notes or remarks..."
                multiline
                rows={3}
                fullWidth
                size="small"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSettlementModalOpen(false)} color="inherit" disabled={submitting}>
            Cancel
          </Button>
          {!submitSuccessMsg && (
            <Button
              onClick={handleOpenConfirm}
              variant="contained"
              color="primary"
              disabled={submitting || !withdrawalAmount}
              startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <AccountBalanceWalletIcon />}
            >
              {submitting ? 'Processing...' : 'Submit Settlement'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog Modal */}
      <Dialog open={confirmModalOpen} onClose={() => setConfirmModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#1e293b', color: 'white', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon sx={{ color: '#f59e0b' }} />
          Confirm Settlement
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ mt: 1 }}>
            Are you sure you want to process this commission settlement?
          </Typography>
          <Paper elevation={0} sx={{ p: 2, my: 2, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2 }}>
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">State Head ID</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {selectedStateHead ? (selectedStateHead.stateHeadId || formatStateHeadId(selectedStateHead._id || selectedStateHead.id, selectedStateHead.stateHeadId)) : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Settlement Amount</Typography>
                <Typography variant="body2" sx={{ fontWeight: 800, color: 'success.main' }}>
                  ₹{withdrawalAmount ? Number(withdrawalAmount).toLocaleString('en-IN') : '0'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Transaction Date</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {settlementDate}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Transaction ID</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {transactionIdInput || 'N/A'}
                </Typography>
              </Grid>
              {remarksInput && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">Remarks</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {remarksInput}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
          <Alert severity="warning" sx={{ fontSize: '0.82rem' }}>
            This action will deduct ₹{withdrawalAmount} from the available balance and record a withdrawal transaction.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outlined" color="inherit" onClick={() => setConfirmModalOpen(false)}>
            Cancel / Edit
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmAndSubmit}
            startIcon={<CheckCircleIcon />}
            sx={{ fontWeight: 700 }}
          >
            Confirm & Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AllStateAdminForCommissionTransactions;